/**
 * As regras puras do "continuar aqui" — e a irmã da troca de motor.
 *
 * Todas leem coisa que pode estar em qualquer estado — uma coluna `Json` que
 * um daemon antigo pode nunca ter preenchido, e um nome que pode ser nulo,
 * vazio ou enorme. Nenhuma delas pode lançar: quem chama está no meio de
 * uma rota, e uma exceção aqui viraria 500 no lugar de um 409 com motivo.
 */
import { MOTORES, ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';

export interface VeredictoDeCapacidade {
  estado: 'aprovada' | 'reprovada' | 'nao_verificada' | 'nao_suportada';
  motivo: string;
}

/**
 * Os nomes das capacidades, num lugar só — e a razão é o modo como errar aqui
 * falha.
 *
 * O relatório é `z.record(string, …)`: qualquer nome é válido para o schema.
 * Uma chave escrita errada não dá erro nenhum — `capacidadeDaMaquina` devolve
 * `null`, que significa "não sei", e quem chama trata como recusa. Na tela isso
 * aparece como a opção simplesmente não sendo oferecida, para sempre, sem log e
 * sem sintoma. É o pior desfecho possível: o produto fica menor e ninguém
 * descobre por quê.
 *
 * Com a constante, `chave` deixa de ser `string` e o erro de digitação não
 * compila. Os nomes são os que a sonda do daemon ESCREVE (`sonda.ts`), não
 * invenção daqui: as duas listas ainda podem divergir, e é por isso que o
 * runtime continua tolerante — o tipo é a guarda, o `null` é a rede.
 */
export const CHAVES_DE_CAPACIDADE = {
  mensagemLocal: 'mensagemLocal',
  responderPermissao: 'responderPermissao',
  responderPergunta: 'responderPergunta',
  aprovarPlano: 'aprovarPlano',
  renomearLocal: 'renomearLocal',
  confirmacaoDeEntrega: 'confirmacaoDeEntrega',
  comandoDeBarra: 'comandoDeBarra',
  trocarModelo: 'trocarModelo',
  continuarAqui: 'continuarAqui',
  mestre: 'mestre',
  /*
    As duas do eixo motor. Nascem aqui ANTES de a sonda escrevê-las (D25) de
    propósito: é este arquivo que decide a grafia, e sem ele o produtor e os
    consumidores a escolheriam cada um por conta. Até a sonda passar a mandá-las
    o veredito é `null` — "não sei" —, que é exatamente a degradação certa para
    uma máquina que ainda não sabe responder.
  */
  codex: 'codex',
  mestreCodex: 'mestreCodex',
  /*
    As do terceiro motor (F16, Interface congelada item 6), pela mesma razão
    das duas de cima: a grafia nasce aqui, antes de a sonda escrevê-las, e os
    vereditos são os mesmos quatro do Codex. `opencode` é a sessão de TRABALHO
    (os PCs, D1); `mestreOpencode` é a mestre, que só mora na VPS.
  */
  opencode: 'opencode',
  mestreOpencode: 'mestreOpencode',
  /** A chave `BYPASS_TOTAL` do daemon (ADR-0023), declarada como a `mestre`. */
  bypassTotal: 'bypassTotal',
} as const;

export type ChaveDeCapacidade = (typeof CHAVES_DE_CAPACIDADE)[keyof typeof CHAVES_DE_CAPACIDADE];

/**
 * Lê um veredito do relatório de capacidades persistido na máquina.
 *
 * Devolve `null` quando a máquina nunca reportou (daemon antigo) ou quando a
 * coluna não tem a forma esperada. `null` é diferente de `nao_suportada`: um é
 * "não sei", o outro é "sei que não". Quem chama trata os dois como recusa,
 * mas a mensagem pode ser diferente.
 */
export function capacidadeDaMaquina(
  capacidades: unknown,
  chave: ChaveDeCapacidade,
): VeredictoDeCapacidade | null {
  if (!capacidades || typeof capacidades !== 'object') return null;
  const mapa = (capacidades as { capacidades?: unknown }).capacidades;
  if (!mapa || typeof mapa !== 'object') return null;
  const bruto = (mapa as Record<string, unknown>)[chave];
  if (!bruto || typeof bruto !== 'object') return null;

  const estado = (bruto as { estado?: unknown }).estado;
  const motivo = (bruto as { motivo?: unknown }).motivo;
  if (
    estado !== 'aprovada' &&
    estado !== 'reprovada' &&
    estado !== 'nao_verificada' &&
    estado !== 'nao_suportada'
  ) {
    return null;
  }
  return { estado, motivo: typeof motivo === 'string' ? motivo : '' };
}

/**
 * O bypass total vale NESTA máquina? (ADR-0023) As duas pontas, como na
 * `MESTRE_PERMITE_PC`: a chave da API E a capacidade `bypassTotal` que o
 * daemon declara. Daemon antigo, que nem manda a capacidade, fica de fora —
 * "não sei" não é "sei que sim".
 */
export function bypassTotalNaMaquina(chaveDaApi: boolean, capacidades: unknown): boolean {
  return chaveDaApi && capacidadeDaMaquina(capacidades, CHAVES_DE_CAPACIDADE.bypassTotal)?.estado === 'aprovada';
}

/** O nome da conversa continuada cabe em 80 caracteres, com sufixo e tudo. */
export const NOME_MAXIMO = 80;
const SUFIXO = ' · do editor';

/**
 * Deriva o nome da conversa que nasce do "continuar aqui".
 *
 * O sufixo existe para as duas não ficarem idênticas na lista — elas convivem,
 * e distinguir qual é qual é metade do valor da funcionalidade. Nome comprido
 * é cortado pelo começo do sufixo, nunca pelo fim: perder " · do editor" faria
 * as duas voltarem a parecer a mesma.
 */
export function nomeDaContinuacao(original: string | null | undefined): string {
  const limpo = (original ?? '').trim();
  if (!limpo) return 'continuada' + SUFIXO;
  const espaco = NOME_MAXIMO - SUFIXO.length;
  return (limpo.length > espaco ? limpo.slice(0, espaco - 1) + '…' : limpo) + SUFIXO;
}

/** Os sufixos que uma troca anterior pode ter deixado no fim do nome. */
const SUFIXOS_DE_TROCA = MOTORES.map((m) => ' · no ' + ROTULO_DO_MOTOR[m]);

/**
 * Deriva o nome da conversa que nasce de uma TROCA DE MOTOR.
 *
 * Mesmas lições do `nomeDaContinuacao`: o sufixo distingue as duas na lista
 * (elas convivem — a antiga fica arquivada, a um desarquivar de distância), e
 * nome comprido é cortado pelo começo, nunca pelo fim, para o sufixo — que é
 * a metade do valor — sobreviver ao corte.
 *
 * Antes de anexar, um sufixo de troca ANTERIOR no fim do nome é removido:
 * ida-e-volta Claude→Codex→Claude não empilha " · no Codex · no Claude Code".
 */
export function nomeDaTroca(original: string | null | undefined, motorDestino: Motor): string {
  const sufixo = ' · no ' + ROTULO_DO_MOTOR[motorDestino];
  let limpo = (original ?? '').trim();
  for (let removeu = true; removeu; ) {
    removeu = false;
    for (const anterior of SUFIXOS_DE_TROCA) {
      if (limpo.endsWith(anterior)) {
        limpo = limpo.slice(0, -anterior.length).trimEnd();
        removeu = true;
      }
    }
  }
  if (!limpo) return 'continuada' + sufixo;
  const espaco = NOME_MAXIMO - sufixo.length;
  return (limpo.length > espaco ? limpo.slice(0, espaco - 1) + '…' : limpo) + sufixo;
}
